import { Corrections } from "../types";

export function isWordApplication() {
  if (typeof Office !== "undefined" && Office.context && Office.context.diagnostics) {
    return Office.context.diagnostics.host === Office.HostType.Word;
  }
  return false;
}

export function getDocumentText(): Promise<string> {
  return Word.run(async (context) => {
    const body = context.document.body;
    body.load("text");

    await context.sync();

    return body.text;
  });
}

export function getSelectedText(): Promise<string> {
  return Word.run(async (context) => {
    const selection = context.document.getSelection();
    selection.load("text");

    await context.sync();

    return selection.text;
  });
}

export async function critiqueWords(corrections: Corrections[]): Promise<void> {
  return Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items/text");
    await context.sync();

    const correctionRecord = new Map(corrections.map((c) => [c.word.toLowerCase(), c.suggestions]));

    for (const paragraph of paragraphs.items) {
      const text = paragraph.text;
      const critiques: Word.Critique[] = [];

      const words = text.split(/\s+/);
      let position = 0;

      for (const word of words) {
        const wordIndex = text.indexOf(word, position);
        if (wordIndex === -1) continue;

        position = wordIndex + word.length;
        const normalized = word.replace(/[.,]$/, "").toLowerCase();
        const suggestions = correctionRecord.get(normalized);

        if (suggestions && suggestions.length > 0) {
          critiques.push({
            colorScheme: Word.CritiqueColorScheme.red,
            start: wordIndex,
            length: word.length,
            popupOptions: {
              titleResourceId: `Misspelling: ${word}`,
              subtitleResourceId: `Did you mean: ${suggestions.join(", ")}?`,
              brandingTextResourceId: `Powered by bolorspell`,
              suggestions: suggestions,
            },
          });
        }
      }

      if (critiques.length > 0) {
        paragraph.insertAnnotations({ critiques });
      }
    }

    await context.sync();
  });
}

export function registerParagraphChangedEvent(
  handler: (event: Word.ParagraphChangedEventArgs) => Promise<void>
) {
  return Word.run(async (context) => {
    const registration = context.document.onParagraphChanged.add(handler);
    await context.sync();
    return registration;
  });
}

export function registerParagraphAddedEvent(
  handler: (event: Word.ParagraphAddedEventArgs) => Promise<void>
) {
  return Word.run(async (context) => {
    const registration = context.document.onParagraphAdded.add(handler);
    await context.sync();
    return registration;
  });
}

export const replaceWordInDocument = async (incorrectWord: string, correctWord: string) => {
  try {
    await Word.run(async (context) => {
      const body = context.document.body;
      const searchResults = body.search(incorrectWord, { matchCase: false, matchWholeWord: true });
      context.load(searchResults, "items");

      await context.sync();

      searchResults.items.forEach((item) => {
        item.insertText(correctWord, "Replace");
      });

      await context.sync();
    });
  } catch (error) {
    console.error("Error replacing word in Word document:", error);
  }
};


export async function findWordOccurrences(word: string): Promise<Word.Range[]> {
  return Word.run(async (context) => {
    const body = context.document.body;
    const searchResults = body.search(word, { matchWholeWord: true, matchCase: false });
    context.load(searchResults, 'items');
    await context.sync();
    return searchResults.items;
  });
}

export async function scrollToWord(word: string): Promise<void> {
  const occurrences = await findWordOccurrences(word);
  if (occurrences.length > 0) {
    occurrences[0].select('Select');
    await Word.run(async (context) => {
      occurrences[0].font.highlightColor = 'Yellow';
      await context.sync();
      setTimeout(async () => {
        await Word.run(async (context) => {
          occurrences[0].font.highlightColor = 'NoColor';
          await context.sync();
        });
      }, 2000);
    });
  } else {
    console.log(`No occurrences of "${word}" found.`);
  }
}